import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiServicepiService from '../services/apiService';
import {
  AArrowDown,
  Camera,
  LogOut,
  Home,
  ChevronRight,
  Search,
  Download,
  Eye,
  Edit,
  X,
  Plus,
  Upload,
  MapPin,
  Calendar,
  User,
  Filter,
  Grid,
  Heart,
  Users,
  Check,
  AlertCircle,
  Image as ImageIcon,
  ExternalLink,
  Trash2,
  RotateCcw,
  ArrowUpDown,
  Save
} from 'lucide-react';

const AdminGallery = () => {
  const { user, logout, isAuthenticated, token, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [loadingActions, setLoadingActions] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    description: '',
    category: 'community',
    date: '',
    photographer: '',
    fallback_type: 'family',
    is_active: true,
    sort_order: 0
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [categories, setCategories] = useState({});
  const [fallbackTypes, setFallbackTypes] = useState({});

  const categoryOptions = {
    'community': { label: 'Vie communautaire', icon: Users, color: 'bg-blue-500' },
    'marriage': { label: 'Mariages & Célébrations', icon: Heart, color: 'bg-pink-500' },
    'pilgrimage': { label: 'Pèlerinages & Retraites', icon: MapPin, color: 'bg-green-500' },
    'culture': { label: 'Culture béninoise', icon: Calendar, color: 'bg-purple-500' },
    'architecture': { label: 'Lieux saints', icon: Camera, color: 'bg-yellow-500' }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchImages();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchImages = async () => {
    try {
      setLoading(true);

      if (!token) {
        console.error('Pas de token d\'authentification');
        setLoading(false);
        return;
      }

      console.log('Token d\'authentification:', token ? 'Présent' : 'Absent');
      console.log('Token value:', token ? token.substring(0, 10) + '...' : 'null');

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active');

      const response = await fetch(`http://localhost:8000/api/gallery-images?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setImages(data.data || []);
        setCategories(data.filters?.categories || {});
        setFallbackTypes(data.filters?.fallback_types || {});
      } else if (response.status === 401) {
        console.error('Token d\'authentification invalide');
        logout(); // Déconnexion automatique si token invalide
      } else {
        console.error('Erreur lors du chargement des images');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (image = null) => {
    if (image) {
      console.log('=== DEBUG OPEN IMAGE MODAL ===');
      console.log('Image data:', image);
      console.log('image.image_url:', image.image_url);
      console.log('image.image_path:', image.image_path);

      setIsEditing(true);
      setSelectedImage(image);
      setFormData({
        title: image.title || '',
        location: image.location || '',
        description: image.description || '',
        category: image.category || 'community',
        date: image.date || '',
        photographer: image.photographer || '',
        fallback_type: image.fallback_type || 'family',
        is_active: image.is_active !== undefined ? image.is_active : true,
        sort_order: image.sort_order || 0
      });

      // Construction de l'URL d'image plus robuste
      let imageUrl = '';
      if (image.image_url) {
        imageUrl = image.image_url;
      } else if (image.image_path) {
        // Si le chemin commence par 'gallery/', on l'utilise directement
        imageUrl = `http://localhost:8000/storage/${image.image_path}`;
      }

      console.log('Preview URL finale:', imageUrl);
      setPreviewUrl(imageUrl);
      setImageUrl('');
    } else {
      setIsEditing(false);
      setSelectedImage(null);
      resetForm();
    }
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setIsEditing(false);
    resetForm();
    setImageFile(null);
    setImageUrl('');
    setPreviewUrl('');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      location: '',
      description: '',
      category: 'community',
      date: '',
      photographer: '',
      fallback_type: 'family',
      is_active: true,
      sort_order: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Redimensionner
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en blob avec extension appropriée
        canvas.toBlob((blob) => {
          // Créer un fichier avec le bon nom et extension
          const fileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
          const compressedFile = new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        }, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Vérifier le type de fichier
        if (!file.type.startsWith('image/')) {
          showNotification('Seuls les fichiers image sont autorisés.', 'error');
          e.target.value = '';
          return;
        }

        // Compresser l'image si elle est trop volumineuse
        let processedFile = file;
        if (file.size > 2 * 1024 * 1024) { // Si > 2MB
          console.log('Compression de l\'image...');
          processedFile = await compressImage(file);
          console.log('Taille avant:', file.size, 'après:', processedFile.size);
        }

        // Vérifier la taille finale
        if (processedFile.size > 5 * 1024 * 1024) { // 5MB max après compression
          showNotification('L\'image est trop volumineuse même après compression.', 'error');
          e.target.value = '';
          return;
        }

        setImageFile(processedFile);
        setImageUrl('');

        // Créer aperçu
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(processedFile);

      } catch (error) {
        console.error('Erreur lors du traitement de l\'image:', error);
        showNotification('Erreur lors du traitement de l\'image.', 'error');
      }
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    setImageFile(null);
    setPreviewUrl(url);

    // Reset file input
    const fileInput = document.getElementById('imageFile');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation côté client
    if (!formData.title.trim()) {
      showNotification('Le titre est requis', 'error');
      return;
    }

    if (!formData.location.trim()) {
      showNotification('Le lieu est requis', 'error');
      return;
    }

    if (!formData.description.trim()) {
      showNotification('La description est requise', 'error');
      return;
    }

    if (!formData.date.trim()) {
      showNotification('La date est requise', 'error');
      return;
    }

    if (!formData.photographer.trim()) {
      showNotification('Le photographe est requis', 'error');
      return;
    }

    if (!isEditing && !imageFile && !imageUrl.trim()) {
      showNotification('Une image ou une URL d\'image est requise', 'error');
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, submit: true }));

      // Debug: vérifier l'authentification
      console.log('=== DEBUG AUTHENTIFICATION ===');
      console.log('User:', user);
      console.log('Token présent:', !!token);
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'null');
      console.log('Is editing:', isEditing);

      // Debug: vérifier les données du formulaire
      console.log('=== DEBUG FORM DATA ===');
      console.log('FormData:', formData);
      console.log('Image file:', imageFile);
      console.log('Image URL:', imageUrl);

      const submitData = new FormData();

      // Ajouter les données du formulaire
      submitData.append('title', formData.title.trim());
      submitData.append('location', formData.location.trim());
      submitData.append('description', formData.description.trim());
      submitData.append('category', formData.category);
      submitData.append('date', formData.date.trim());
      submitData.append('photographer', formData.photographer.trim());
      submitData.append('fallback_type', formData.fallback_type);
      submitData.append('is_active', formData.is_active ? '1' : '0');
      submitData.append('sort_order', formData.sort_order.toString());

      // Debug: afficher les données FormData
      console.log('=== DEBUG SUBMIT DATA ===');
      for (let [key, value] of submitData.entries()) {
        console.log(key, ':', value);
      }

      // Ajouter l'image
      if (imageFile) {
        submitData.append('image', imageFile);
        console.log('Image file ajoutée:', imageFile.name);
      } else if (imageUrl.trim()) {
        submitData.append('image_url', imageUrl.trim());
        console.log('Image URL ajoutée:', imageUrl.trim());
      }

      const url = isEditing
        ? `http://localhost:8000/api/public/gallery-simple/${selectedImage.id}`
        : 'http://localhost:8000/api/public/gallery-simple'; // Route simple qui fonctionne

      // Utiliser FormData pour les deux cas (création et modification)
      const requestOptions = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: submitData
      };

      console.log('=== DEBUG REQUEST ===');
      console.log('URL:', url);
      console.log('Headers:', requestOptions.headers);

      const response = await fetch(url, requestOptions);

      // Essayer de parser la réponse même en cas d'erreur
      let data;
      const responseText = await response.text();
      console.log('=== DEBUG RESPONSE TEXT ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('Response Text:', responseText);

      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Erreur de parsing JSON:', parseError);
        data = { message: 'Réponse invalide du serveur', errors: { parse: [responseText] } };
      }

      if (response.ok && data.success) {
        showNotification(
          isEditing ? 'Image mise à jour avec succès' : 'Image ajoutée avec succès',
          'success'
        );
        closeImageModal();
        fetchImages();
      } else {
        console.log('Erreurs de validation:', data.errors);
        const errorMessage = data.message || 'Erreur lors de la sauvegarde';
        if (data.errors) {
          const firstError = Object.values(data.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            showNotification(firstError[0], 'error');
          } else {
            showNotification(errorMessage, 'error');
          }
        } else {
          showNotification(errorMessage, 'error');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState.submit;
        return newState;
      });
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      const response = await fetch(`http://localhost:8000/api/gallery-images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        showNotification('Image supprimée avec succès', 'success');
        fetchImages();
      } else {
        showNotification('Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Erreur de connexion', 'error');
    }
  };

  const toggleImageStatus = async (imageId, currentStatus) => {
    try {
      setLoadingActions(prev => ({ ...prev, [imageId]: 'toggle' }));

      const response = await fetch(`http://localhost:8000/api/gallery-images/${imageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        showNotification(
          !currentStatus ? 'Image activée' : 'Image désactivée',
          'success'
        );
        fetchImages();
      } else {
        showNotification('Erreur lors de la modification', 'error');
      }
    } catch (error) {
      console.error('Toggle error:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState[imageId];
        return newState;
      });
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active');

      const response = await fetch(`http://localhost:8000/api/gallery-images/export/images?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Créer un fichier CSV
        const headers = Object.keys(data.data[0] || {});
        const csvContent = [
          headers.join(','),
          ...data.data.map(row => headers.map(header =>
            `"${String(row[header] || '').replace(/"/g, '""')}"`
          ).join(','))
        ].join('\n');

        // Télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', data.filename.replace('.xlsx', '.csv'));
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        showNotification('Export réalisé avec succès', 'success');
      } else {
        showNotification('Erreur lors de l\'export', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getCategoryIcon = (category) => {
    const CategoryIcon = categoryOptions[category]?.icon || Camera;
    return <CategoryIcon className="w-4 h-4" />;
  };

  const getCategoryColor = (category) => {
    return categoryOptions[category]?.color || 'bg-gray-500';
  };

  useEffect(() => {
    fetchImages();
  }, [searchTerm, categoryFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2 text-gray-600 text-sm">
                <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium text-gray-900 hidden sm:inline">Dashboard</span>
                <span className="font-medium text-gray-900 sm:hidden">Admin</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-purple-600 font-semibold text-sm sm:text-base">
                  <span className="hidden sm:inline">Gestion Galerie</span>
                  <span className="sm:hidden">Galerie</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 w-full sm:w-auto">
              <div className="text-left sm:text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Camera className="w-3 h-3 mr-1 text-blue-500" />
                  <span className="hidden sm:inline">Gestion Galerie FSF</span>
                  <span className="sm:hidden">Galerie FSF</span>
                </p>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Retour au Dashboard</span>
                  <span className="sm:hidden">Retour</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                  <span className="sm:hidden">Quitter</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Titre et bouton d'ajout */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="hidden sm:inline">Gestion de la Galerie Photo</span>
              <span className="sm:hidden">Galerie Photo</span>
            </h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg text-justify">
              <span className="hidden sm:inline">Gérez les images de la galerie FSF</span>
              <span className="sm:hidden">Images FSF</span>
            </p>
          </div>

          {!isReadOnly && (
          <button
            onClick={() => openImageModal()}
            className="flex items-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-full sm:w-auto justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Ajouter une image</span>
            <span className="sm:hidden">Nouvelle image</span>
          </button>
          )}
        </div>

        {/* Filtres et actions */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 space-y-4 lg:space-y-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              <span className="hidden sm:inline">Filtres et actions</span>
              <span className="sm:hidden">Filtres</span>
            </h3>
            <button
              onClick={handleExport}
              className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm w-full sm:w-auto justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Exporter CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="lg:col-span-3 xl:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Titre, lieu ou photographe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 sm:pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Filtre par catégorie */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Catégorie
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">
                  <span className="hidden sm:inline">Toutes les catégories</span>
                  <span className="sm:hidden">Toutes</span>
                </option>
                {Object.entries(categoryOptions).map(([key, option]) => (
                  <option key={key} value={key}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">
                  <span className="hidden sm:inline">Tous les statuts</span>
                  <span className="sm:hidden">Tous</span>
                </option>
                <option value="active">Actives</option>
                <option value="inactive">Inactives</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des images */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              <span className="hidden sm:inline">Images de la galerie</span>
              <span className="sm:hidden">Images</span>
              <span className="ml-2 text-sm font-normal">({images.length})</span>
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des images...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune image trouvée</p>
              {!isReadOnly && (
              <button
                onClick={() => openImageModal()}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Ajouter la première image
              </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 p-4 sm:p-6">
              {images.map((image) => (
                <div key={image.id} className="relative group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  {/* Image */}
                  <div className="relative h-40 sm:h-48 overflow-hidden">
                    <img
                      src={image.image_url || '/assets/images/couple.png'}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = '/assets/images/couple.png';
                      }}
                    />

                    {/* Overlay pour desktop */}
                    {!isReadOnly && (
                    <div className="hidden sm:block absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openImageModal(image)}
                            className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteImage(image.id)}
                            className="p-2 bg-red-500/80 backdrop-blur-sm rounded-full text-white hover:bg-red-600/80 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Badge catégorie */}
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getCategoryColor(image.category)}`}>
                        {categoryOptions[image.category]?.label}
                      </span>
                    </div>

                    {/* Badge statut */}
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${image.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {image.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Informations */}
                  <div className="p-3 sm:p-4">
                    <h4 className="font-bold text-gray-800 mb-1 line-clamp-1 text-sm sm:text-base">
                      {image.title}
                    </h4>

                    <div className="flex items-center text-gray-600 text-xs sm:text-sm mb-2">
                      <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{image.location}</span>
                    </div>

                    <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2 text-justify">
                      {image.description}
                    </p>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">{image.date}</span>
                        <span className="sm:hidden">{new Date(image.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                      </span>
                      <span className="flex items-center truncate max-w-[50%]">
                        <span className="hidden sm:inline">📸 </span>
                        <span className="truncate">{image.photographer}</span>
                      </span>
                    </div>

                    {/* Actions rapides */}
                    {!isReadOnly && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleImageStatus(image.id, image.is_active)}
                        disabled={loadingActions[image.id]}
                        className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${image.is_active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } ${loadingActions[image.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {loadingActions[image.id] ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mx-auto"></div>
                        ) : (
                          image.is_active ? 'Désactiver' : 'Activer'
                        )}
                      </button>

                      <button
                        onClick={() => openImageModal(image)}
                        className="flex-1 py-2 px-3 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md text-xs font-medium transition-colors"
                      >
                        Modifier
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'ajout/édition d'image */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Header du modal */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <h2 className="text-lg sm:text-2xl font-bold flex items-center">
                    <Camera className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0" />
                    <span className="hidden sm:inline">
                      {isEditing ? 'Modifier l\'image' : 'Ajouter une image'}
                    </span>
                    <span className="sm:hidden">
                      {isEditing ? 'Modifier' : 'Ajouter'}
                    </span>
                  </h2>
                  <p className="text-blue-100 mt-1 text-sm">
                    <span className="hidden sm:inline">
                      {isEditing ? 'Modifiez les informations de l\'image' : 'Ajoutez une nouvelle image à la galerie'}
                    </span>
                    <span className="sm:hidden">
                      {isEditing ? 'Modifier l\'image' : 'Nouvelle image'}
                    </span>
                  </p>
                </div>
                <button
                  onClick={closeImageModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Image */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Image</h3>

                {/* Aperçu */}
                {previewUrl && (
                  <div className="relative w-full h-40 sm:h-48 bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={previewUrl}
                      alt="Aperçu"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Upload de fichier */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier image
                  </label>
                  <div className="flex flex-col space-y-2">
                    <input
                      type="file"
                      id="imageFile"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-500">
                      Formats acceptés : JPEG, PNG, JPG, GIF, WebP. Taille max : 25MB
                    </p>
                  </div>
                </div>

                {/* OU URL */}
                <div className="text-center text-gray-500 text-sm">OU</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de l'image
                  </label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={handleImageUrlChange}
                    placeholder="https://exemple.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {Object.entries(categoryOptions).map(([key, option]) => (
                      <option key={key} value={key}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de fallback *
                  </label>
                  <select
                    name="fallback_type"
                    value={formData.fallback_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="family">Famille</option>
                    <option value="couple">Couple</option>
                    <option value="church">Église</option>
                    <option value="prayer">Prière</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="text"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    placeholder="Mars 2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photographe *
                  </label>
                  <input
                    type="text"
                    name="photographer"
                    value={formData.photographer}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ordre de tri
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div className="flex items-center space-x-3 pt-4 md:pt-7">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Image active
                  </label>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={closeImageModal}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loadingActions.submit}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
                >
                  {loadingActions.submit ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEditing ? 'Modifier' : 'Ajouter'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 left-4 sm:left-auto p-3 sm:p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
          {notification.type === 'success' ? <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
          <span className="text-sm sm:text-base">{notification.message}</span>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;